import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTypewriter } from '@/composables/useTypewriter';
import { UserIcon, PaperAirplaneIcon, PlusIcon } from '@heroicons/vue/solid';
import type { AuraAction, AuraMessage } from '@/types';
import { formatAuraDateLabel, formatAuraMetric } from '@/utils/auraPresentation';
import AuraActionCard from './components/AuraActionCard.vue';
import AuraChartBlock from './components/AuraChartBlock.vue';
import AuraInsightCard from './components/AuraInsightCard.vue';
import AuraRichText from './components/AuraRichText.vue';
import AuraVisualizationPanel from './components/AuraVisualizationPanel.vue';

// AURA Icons
import auraMainIcon from '@/assets/icons/aura-main.svg';
import auraConversationIcon from '@/assets/icons/aura-conversation.svg';

interface Props {
    messages?: AuraMessage[];
    isLoading?: boolean;
    disabled?: boolean;
    emptyPrompts?: string[];
    contextTitle?: string;
    loadingMessage?: string;
}

const MAX_MESSAGE_LENGTH = 2000;


export default /*@__PURE__*/_defineComponent({
  __name: 'AuraChatLayout',
  props: {
    messages: { type: Array, required: false, default: () => [] },
    isLoading: { type: Boolean, required: false, default: false },
    disabled: { type: Boolean, required: false, default: false },
    emptyPrompts: { type: Array, required: false, default: () => [] },
    contextTitle: { type: String, required: false, default: '' },
    loadingMessage: { type: String, required: false, default: '' }
  },
  emits: ["send", "retry-failed-question", "send-with-filter"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

/**
 * AURA Chat Layout - Main chat interface layout
 * [FE] Implementar layout principal do chat
 */
const props = __props;
const { t, locale } = useI18n();

const emit = __emit;

const inputMessage = ref('');
const messagesContainer = ref<HTMLElement | null>(null);
const textareaRef = ref<HTMLTextAreaElement | null>(null);
const canSend = computed(() => {
    return inputMessage.value.trim().length > 0 && !props.isLoading && !props.disabled;
});

const messageLength = computed(() => inputMessage.value.length);
const remainingCharacters = computed(() => Math.max(MAX_MESSAGE_LENGTH - messageLength.value, 0));

const hasMessages = computed(() => props.messages.length > 0);

const adjustTextareaHeight = () => {
    if (textareaRef.value) {
        textareaRef.value.style.height = 'auto';
        textareaRef.value.style.height = `${Math.min(textareaRef.value.scrollHeight, 120)}px`;
    }
};

const retryFailedQuestion = (auraMsgIndex: number) => {
    const userMsgIndex = auraMsgIndex - 1;
    let questionText = '';
    if (userMsgIndex >= 0 && props.messages[userMsgIndex]?.type === 'user') {
        questionText = props.messages[userMsgIndex].content;
    }

    inputMessage.value = questionText;

    nextTick(() => {
        textareaRef.value?.focus();
        adjustTextareaHeight();
    });

    emit('retry-failed-question', {
        userMsgIndex,
        auraMsgIndex,
        question: questionText,
    });
};

const sendMessage = () => {
    if (!canSend.value) return;

    const message = inputMessage.value.trim();
    emit('send', message);
    inputMessage.value = '';

    // Reset textarea height
    if (textareaRef.value) {
        textareaRef.value.style.height = 'auto';
    }

    // Auto-scroll to bottom
    nextTick(() => {
        scrollToBottom();
    });
};

const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        sendMessage();
    }
};

const handleInput = (e: Event) => {
    const target = e.target as HTMLTextAreaElement;
    target.style.height = 'auto';
    target.style.height = `${Math.min(target.scrollHeight, 120)}px`;
};

const scrollToBottom = () => {
    if (messagesContainer.value) {
        messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
};

// Auto-scroll when messages change
watch(
    () => props.messages.length,
    () => {
        nextTick(() => {
            scrollToBottom();
        });
    },
);

const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString(locale.value, {
        hour: '2-digit',
        minute: '2-digit',
    });
};

const selectPrompt = (prompt: string) => {
    inputMessage.value = prompt;
    if (textareaRef.value) {
        textareaRef.value.focus();
    }
};

const focusComposer = () => {
    textareaRef.value?.focus();
};

const executeAction = (action: AuraAction) => {
    const followUpQuestion = action.followUpQuestion?.trim();
    if (!followUpQuestion || props.isLoading || props.disabled) return;
    emit('send', followUpQuestion);
};

const getFilterDisambiguationOptions = (message: AuraMessage) => {
    return (
        message.response?.status?.details?.options ||
        message.response?.status?.filter_conflict?.matches ||
        []
    );
};

const getFilterDisambiguationFilterKey = (message: AuraMessage) => {
    return message.response?.status?.filter_conflict?.filterKey || 'userId';
};

const getFilterDisambiguationLabel = (message: AuraMessage) => {
    const code = message.response?.status?.code;
    if (code === 'AGENT_NAME_AMBIGUOUS') {
        return t('aura.chat.selectAgent');
    }
    return t('aura.chat.selectFilterOption');
};

const getKpiUnit = (message: AuraMessage, unit?: string): string | undefined => {
    return (
        unit || message.response?.chart?.unit || message.response?.visualization?.charts[0]?.unit
    );
};

// Typewriter para loading message — anima quando isLoading=true e loadingMessage muda
const { displayed: typewriterText } = useTypewriter(() =>
    props.isLoading ? (props.loadingMessage ?? '') : '',
);

__expose({
    focusComposer,
});

const __returned__ = { props, t, locale, emit, inputMessage, messagesContainer, textareaRef, MAX_MESSAGE_LENGTH, canSend, messageLength, remainingCharacters, hasMessages, adjustTextareaHeight, retryFailedQuestion, sendMessage, handleKeydown, handleInput, scrollToBottom, formatTime, selectPrompt, focusComposer, executeAction, getFilterDisambiguationOptions, getFilterDisambiguationFilterKey, getFilterDisambiguationLabel, getKpiUnit, typewriterText, get UserIcon() { return UserIcon }, get PaperAirplaneIcon() { return PaperAirplaneIcon }, get PlusIcon() { return PlusIcon }, get formatAuraDateLabel() { return formatAuraDateLabel }, get formatAuraMetric() { return formatAuraMetric }, AuraActionCard, AuraChartBlock, AuraInsightCard, AuraRichText, AuraVisualizationPanel, get auraMainIcon() { return auraMainIcon }, get auraConversationIcon() { return auraConversationIcon } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})